import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';
import { trapTab } from '../../utils/focusTrap';
import { playableSource } from '../../utils/events/briefingCache';

/*
 * What to do at a waypoint, shown the moment the server decides the player
 * has arrived (routes/event_live.py): the search radius, set out with a cord
 * and a stake, then search inside it. When the waypoint names a briefing
 * video (routes/event_briefings.py, PR12) it plays here -- from the copy kept
 * on the phone by "Prepare for the event" when there is one, so it plays
 * with no signal -- beside the same words.
 *
 * Accessible like the app's other dialogs: a labelled modal, focus on its
 * heading, Tab kept inside, Escape closes, focus returned.
 */
export default function BriefingModal({ arrival, onDone, missionId = null }) {
  const { t } = useTranslation('events');
  const box = useRef(null);
  const heading = useRef(null);
  const [video, setVideo] = useState(null);
  useEffect(() => {
    const before = document.activeElement;
    heading.current?.focus();
    return () => { before?.focus?.(); };
  }, []);
  const briefingId = arrival?.briefing_id || null;
  useEffect(() => {
    if (!briefingId || !missionId) return undefined;
    let live = true;
    let release = () => {};
    api.get(`/events/${missionId}/briefings`)
      .then((r) => (r.data.items || []).find((b) => b.briefing_id === briefingId))
      .then((b) => (b ? playableSource(b) : null))
      .then((source) => {
        if (!source) return;
        if (live) {
          release = source.revoke;
          setVideo(source);
        } else {
          source.revoke();
        }
      })
      .catch(() => {});
    return () => { live = false; release(); };
  }, [briefingId, missionId]);
  if (!arrival) return null;
  const finish = arrival.kind === 'finish';
  const start = arrival.kind === 'start';
  const radius = arrival.search_radius_m;
  return createPortal(
    <div className="hub-drawer-backdrop" style={ms.backdrop} data-testid="event-briefing-backdrop">
      <div ref={box} role="dialog" aria-modal="true" aria-labelledby="event-briefing-title" style={ms.box}
        data-testid="event-briefing" data-kind={arrival.kind}
        onKeyDown={(e) => {
          if (e.key === 'Escape') { e.preventDefault(); onDone(); }
          trapTab(box.current, e);
        }}>
        <h2 id="event-briefing-title" ref={heading} tabIndex={-1} style={{ marginTop: 0 }}>
          {finish ? t('briefing.finishTitle') : start ? t('briefing.startTitle') : t('briefing.title')}
        </h2>
        {arrival.label && <p dir="auto"><strong>{arrival.label}</strong></p>}
        {video && (
          // A training video without speech; its steps are written out below.
          <video src={video.src} controls playsInline style={{ width: '100%', borderRadius: 8, background: '#000' }}
            data-testid="event-briefing-video" data-cached={video.cached ? 'true' : 'false'} />
        )}
        {!finish && !start && (
          <ol style={{ paddingInlineStart: 20 }} data-testid="event-briefing-steps">
            <li>{t('briefing.stake')}</li>
            <li>{radius ? t('briefing.cord', { radius }) : t('briefing.cordDefault')}</li>
            <li>{t('briefing.search')}</li>
            <li>{t('briefing.photograph')}</li>
          </ol>
        )}
        {start && <p>{t('briefing.startText')}</p>}
        {finish && <p>{t('briefing.finishText')}</p>}
        <p style={ms.safety} role="note">{t('safety.neverEat')}</p>
        <button type="button" className="pn-btn" style={ms.done} onClick={onDone} data-testid="event-briefing-done">
          {t('briefing.done')}
        </button>
      </div>
    </div>,
    document.body,
  );
}

const ms = {
  backdrop: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)', zIndex: 3300, display: 'grid',
    placeItems: 'center', padding: 16 },
  box: { background: 'var(--c-surface, #fff)', color: 'var(--c-text-fg, #111)', borderRadius: 14, padding: 20,
    maxWidth: 440, width: '100%', boxShadow: '0 8px 30px rgba(0,0,0,.4)' },
  safety: { fontSize: 13, background: '#fff3e0', color: '#5d4037', padding: '8px 10px', borderRadius: 8 },
  done: { minHeight: 44, width: '100%', marginTop: 8 },
};
